import type { ReactElement } from 'react';
import React, { useLayoutEffect, useRef } from 'react';

import { integrationTesting } from '@swf/core/utils';

interface ChildData {
  ref: React.RefObject<HTMLDivElement>;
  key: string;
}

interface DomNodePosition {
  offsetLeft: number;
  offsetTop: number;
}

interface DomNodesPosition {
  [key: string]: DomNodePosition;
}

const calculateDomNodesPosition = (children: React.ReactNode) => {
  const domNodesPosition: DomNodesPosition = {};

  React.Children.forEach(children, (c) => {
    const child = c as ChildData;
    const domNode = child?.ref.current;

    if (!domNode) {
      return;
    }

    domNodesPosition[child.key] = {
      offsetLeft: domNode.offsetLeft,
      offsetTop: domNode.offsetTop
    };
  });
  return domNodesPosition;
};

const AnimateClubs: React.FC = ({ children }) => {
  const prevBoundingBoxRef = useRef<DomNodesPosition | null>(null);

  useLayoutEffect(() => {
    if (integrationTesting) return;

    const nextBoundingBox = calculateDomNodesPosition(children);
    const prevBoundingBox = prevBoundingBoxRef.current;
    if (prevBoundingBox === null) {
      prevBoundingBoxRef.current = nextBoundingBox;
      return;
    }

    React.Children.forEach(children, (c, i) => {
      const child = c as ChildData;
      const domNode = child.ref.current;

      if (!domNode) {
        return;
      }

      const prevBox = prevBoundingBox[child?.key];
      const changeInX = (prevBox?.offsetLeft ?? domNode.offsetLeft) - domNode.offsetLeft;
      const changeInY = (prevBox?.offsetTop ?? domNode.offsetTop) - domNode.offsetTop;

      // Invert child to old position
      domNode.style.opacity = prevBox ? '' : '0';
      domNode.style.transform = `translate(${changeInX}px, ${changeInY}px)`;
      domNode.style.transition = 'transform 0s';

      requestAnimationFrame(() => {
        // After the previous frame, remove the transition to play the animation
        domNode.style.opacity = '';
        domNode.style.transform = '';
        domNode.style.zIndex = Math.round(Math.abs(changeInY)).toString();
        domNode.style.transition = `transform 1s ${i * 5}ms, opacity 1s 0.4s`;
      });
    });
    prevBoundingBoxRef.current = nextBoundingBox;
  }, [children]);

  return children as ReactElement;
};

export default AnimateClubs;
